import React from 'react';
import { ChevronRightIcon } from 'lucide-react';
import { DeleteItemIcon } from '@/assets/icons/button';
import { DraggableDotsVerticalIcon } from '@/assets/ui/dots';
import { CompletionBadge, InlineEditableText } from '@/common/components/course';
import { useInlineEdit } from '@/common/hooks/useInlineEdit';
import ProgressUtilityService from '@/services/course/progress-utility-service';
import CourseUtilityService from '@/services/course/course-utility-service';
import { SubunitData, SubunitId } from 'course-types';

interface SubunitRowProps {
  subunit: SubunitData & { id: string };
  unitId: string;
  activeCourseId: string;
  isExpanded: boolean;
  isAdminView?: boolean;
  isEditable?: boolean;
  isEditing?: boolean;
  dragAttributes?: any;
  dragListeners?: any;
  onToggle: (subunitId: string) => void;
  onEdit?: (subunitId: string, newName: string) => Promise<void>;
  onEditStart?: (subunitId: string) => void;
  onEditEnd?: () => void;
  onDelete?: (subunitId: string, unitId: string) => void;
  disableDelete?: boolean;
  children?: React.ReactNode;
}

/**
 * SubunitRow component displays a subunit header with expand/collapse,
 * inline editing, and nested activities.
 */
export default function SubunitRow({
  subunit,
  unitId,
  activeCourseId,
  isExpanded,
  isAdminView,
  isEditable = true,
  isEditing,
  dragAttributes,
  dragListeners,
  onToggle,
  onEdit,
  onEditStart,
  onEditEnd,
  onDelete,
  disableDelete,
  children,
}: SubunitRowProps) {
  // Create edit hook for inline editing
  const editHook = useInlineEdit({
    initialValue: subunit.name,
    onSave: async (newName: string) => {
      if (onEdit) {
        await onEdit(subunit.id, newName);
      }
      if (onEditEnd) {
        onEditEnd();
      }
    },
    onCancel: () => {
      if (onEditEnd) {
        onEditEnd();
      }
    },
  });

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEditStart) {
      onEditStart(subunit.id);
    }
    editHook.startEditing(subunit.name);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disableDelete) return;
    if (onDelete) {
      onDelete(subunit.id, unitId);
    }
  };

  // Calculate completion status
  const total = ProgressUtilityService.getSubunitTrackableTotal(subunit);
  const done = ProgressUtilityService.getSubunitCompletedActivities(
    activeCourseId as any,
    subunit.id as SubunitId,
    subunit,
  );
  const isCompleted = total === 0 || done === total;

  // Calculate activity and resource counts
  const activityCount = CourseUtilityService.calculateSubunitActivities(subunit);
  const resourceCount = CourseUtilityService.calculateSubunitResources(subunit);

  return (
    <>
      {/* More padding on mobile for better spacing */}
      <div
        className='flex flex-row w-full items-center p-4 md:p-2 gap-4 justify-between hover:bg-surface_gray_50 rounded-lg cursor-pointer'
        onClick={() => onToggle(subunit.id)}
      >
        {/* Mobile fix: Restructured layout so completion badge shares row with activity count. Extra vertical spacing on mobile */}
        <div className='flex flex-row items-center gap-1 flex-1'>
          {isAdminView && isEditable && (
            <div {...dragAttributes} {...dragListeners} className='cursor-move'>
              <DraggableDotsVerticalIcon className='w-4 h-4 text-text_gray_900' />
            </div>
          )}
          <button className='p-2 rounded-[8px] w-fit h-fit hover:bg-surface_gray_100 transition-colors'>
            <div
              className='flex items-center justify-center w-5 h-5 transition-transform duration-200'
              style={{
                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              }}
            >
              <ChevronRightIcon className='w-5 h-5 text-text_gray_600' />
            </div>
          </button>

          <div className='flex flex-col gap-2 md:gap-1 items-start flex-1'>
            {isAdminView && isEditable ? (
              <InlineEditableText
                text={subunit.name}
                isEditing={!!(isEditing && editHook.isEditing)}
                editValue={editHook.editValue}
                onEditClick={handleEditClick}
                onChange={editHook.handleChange}
                onBlur={editHook.handleSave}
                onKeyDown={editHook.handleKeyDown}
                id={subunit.id}
              />
            ) : (
              <h3 className='text-text_gray_900 self-stretch text-sm not-italic font-semibold leading-5'>
                {subunit.name}
              </h3>
            )}
            {/* Completion badge on same row as activity count, wider gap on mobile so they don't crowd */}
            <div className='flex items-center justify-between w-full gap-3 md:gap-2'>
              <span className='text-text_gray_600 text-xs not-italic font-normal leading-[18px]'>
                {`${activityCount} activit${activityCount === 1 ? 'y' : 'ies'}${resourceCount > 0 ? ` • ${resourceCount} ${resourceCount === 1 ? 'resource' : 'resources'}` : ''}`}
              </span>
              {!isAdminView && isCompleted && (
                <div className='md:hidden'>
                  <CompletionBadge />
                </div>
              )}
            </div>
          </div>
        </div>

        {isAdminView && isEditable && (
          <button
            onClick={handleDeleteClick}
            disabled={!!disableDelete}
            className={`flex items-center justify-center p-2 rounded-[8px] w-fit h-fit group transition-colors ${
              disableDelete ? 'opacity-50 cursor-not-allowed' : 'hover:bg-surface_red_100'
            }`}
          >
            <DeleteItemIcon className='h-5 w-5 text-[#A4A7AE] group-hover:text-red-600' />
          </button>
        )}
        {!isAdminView && isCompleted && (
          <div className='hidden md:block'>
            <CompletionBadge />
          </div>
        )}
      </div>
      {isExpanded && children}
    </>
  );
}
